import { computed } from 'vue';

import { StreamerService } from '../services/StreamerService';
import { useStore } from '../store';
import { ProductNotFoundError, VoiceChangeFailedError } from '../types/errors';
import { TwitchBitsTransaction } from '../types/TwitchExtension';

export const useApplyVoice = (): {
	handleClickApplyVoice: () => Promise<void>;
	handleTransactionCancelled: () => void;
	handleTransactionComplete: (transaction: TwitchBitsTransaction) => void;
} => {
	const store = useStore();

	const productSku = computed(() => {
		if (import.meta.env.VITE_FORCE_1BIT_TRANSACTION === 'true') {
			return 'vm1bits';
		}

		return `vm${store.selectedVoice?.price}bits`;
	});

	const productSkuExists = async () => {
		const products = await window.Twitch?.ext.bits.getProducts();
		if (!products) {
			return false;
		}

		return products.some((product) => product.sku === productSku.value);
	};

	const handleClickApplyVoice = async () => {
		if (!store.selectedVoice) {
			return;
		}

		if (!(await productSkuExists())) {
			const productError = ProductNotFoundError.report({
				context: {
					productSku: productSku.value,
				},
			});
			productError.debug();
			store.processingCanceled(productError);

			return;
		}

		store.processingStarted();
		window.Twitch?.ext.bits.useBits(productSku.value);
	};

	const handleTransactionComplete = async (
		transaction: TwitchBitsTransaction,
	) => {
		if (transaction.initiator !== 'current_user') {
			console.info(
				'Received transaction is not initiated by the current user, skipping',
			);

			return;
		}

		try {
			if (!store.selectedVoice) {
				throw new Error('No voice selected when applying voice change');
			}

			await StreamerService.changeVoiceBits(
				store.selectedVoice.id,
				store.defaultTimeSeconds,
				transaction,
			);

			store.processingFinished();
		} catch (error) {
			const voiceChangeError = VoiceChangeFailedError.report({
				description: 'Voice Change request failed',
				cause: error,
				context: {
					transaction,
				},
			});
			voiceChangeError.debug();

			store.processingCanceled(voiceChangeError);
		}
	};

	const handleTransactionCancelled = () => {
		store.processingCanceled();
	};

	return {
		handleClickApplyVoice,
		handleTransactionCancelled,
		handleTransactionComplete,
	};
};
